import {
  CheckCircleFilled,
  DownloadOutlined,
  FilePdfOutlined,
  MessageOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import {
  type ArrearsFilter,
  billsCount,
  cubicMeters,
  daysCount,
  describeError,
  dirhams,
  downloadReportPdf,
  filterArrears,
  fullDate,
  type MonthKey,
  membersCount,
  monthYear,
  openReportPdf,
  present,
  ReportError,
  reportYears,
  todayInMorocco,
  totalDue,
  weekChoices,
} from '@horof/shared';
import {
  Alert,
  App,
  Button,
  Segmented,
  Select,
  Skeleton,
  Table,
  Tag,
} from 'antd';
import { type ReactNode, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery } from 'urql';
import {
  AnnualReportLinkDocument,
  type ArrearRow,
  ArrearsDocument,
  MonthlyReportDocument,
  type MonthRow,
  RecordReminderDocument,
  type ReminderRow,
  RemindersDueDocument,
} from './reports-data';
import { useFitColumns } from './table-fit';
import { PageHeader } from './ui';

/**
 * «التقارير»: billed and collected month by month, the late payers, today's
 * reminders, and the annual and weekly reports (PDF).
 */
export function ReportsScreen() {
  return (
    <section className="desk-page desk-summary">
      <PageHeader
        title="التقارير"
        description="تقارير الماء: الشهرية والسنوية والأسبوعية، المتأخرون، تذكيرات اليوم."
      />
      <div className="desk-summary-grid">
        <MonthlyCard />
        <AnnualCard />
        <ArrearsCard />
        <WeeklyCard />
        <RemindersCard />
      </div>
    </section>
  );
}

function Card({
  title,
  hint,
  wide,
  extra,
  children,
}: {
  title: string;
  hint?: ReactNode;
  wide?: boolean;
  extra?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className={`desk-card${wide ? ' is-wide' : ''}`}>
      <h2 className="desk-card-title">
        {title}
        {extra && <span className="desk-card-extra">{extra}</span>}
      </h2>
      {hint && <div className="field-hint">{hint}</div>}
      {children}
    </section>
  );
}

function MonthlyCard() {
  const navigate = useNavigate();
  const [{ data, error }] = useQuery({
    query: MonthlyReportDocument,
    requestPolicy: 'cache-and-network',
  });
  return (
    <Card
      wide
      title="التقرير الشهري"
      hint="الفوترة: فواتير الشهر. المُحصّل: كل ما أُدّي خلال الشهر، ولو لفواتير سابقة. اضغط على شهر لإغلاقه."
    >
      {error && <Alert type="error" showIcon message="تعذّر تحميل التقرير" />}
      <Table<MonthRow>
        rowKey="month"
        size="small"
        loading={!data && !error}
        dataSource={present(data?.monthlyReport?.nodes)}
        pagination={{ pageSize: 12, size: 'small' }}
        rowClassName={() => 'is-clickable'}
        onRow={(m) => ({
          onClick: () =>
            navigate(`/close?m=${m.month.slice(0, 7) as MonthKey}`),
        })}
        columns={[
          {
            key: 'month',
            title: 'الشهر',
            render: (_, m) => monthYear(m.month),
          },
          {
            key: 'bills',
            title: 'الفواتير',
            render: (_, m) => (m.billsCount ? billsCount(m.billsCount) : '—'),
          },
          {
            key: 'consumption',
            title: 'الاستهلاك',
            render: (_, m) => cubicMeters(m.consumption),
          },
          {
            key: 'billed',
            title: 'الفوترة',
            render: (_, m) => dirhams(m.billedTotal),
          },
          {
            key: 'collected',
            title: 'المُحصّل',
            render: (_, m) => <strong>{dirhams(m.paymentsTotal)}</strong>,
          },
        ]}
      />
    </Card>
  );
}

function AnnualCard() {
  const [{ data: months }] = useQuery({ query: MonthlyReportDocument });
  const years = reportYears(present(months?.monthlyReport?.nodes));
  const [year, setYear] = useState<number | null>(null);
  const selected = year ?? years[0] ?? null;
  const [{ data, fetching, error }] = useQuery({
    query: AnnualReportLinkDocument,
    variables: { year: selected ?? 0 },
    pause: selected === null,
  });
  return (
    <Card
      title="التقرير المالي السنوي"
      hint="للجمع العام: الماء (الفوترة والتحصيل)، مالية الجمعية والمشاريع، أرصدة الحسابات، والمقارنة مع السنة السابقة."
    >
      {years.length > 0 && (
        <Segmented<number>
          value={selected ?? undefined}
          onChange={setYear}
          options={years
            .slice(0, 5)
            .map((y) => ({ value: y, label: String(y) }))}
        />
      )}
      {error && <Alert type="error" message="تعذّر إنشاء الرابط" />}
      <Button
        type="primary"
        icon={<FilePdfOutlined />}
        loading={fetching}
        disabled={!data?.annualReportLink}
        href={data?.annualReportLink}
        target="_blank"
      >
        فتح تقرير {selected ?? ''}
      </Button>
    </Card>
  );
}

function WeeklyCard() {
  const { message } = App.useApp();
  const choices = useMemo(() => weekChoices(todayInMorocco()), []);
  const [end, setEnd] = useState(choices[0].end);
  const [busy, setBusy] = useState<'open' | 'download' | null>(null);
  const label = choices.find((c) => c.end === end)?.label ?? '';

  async function run(kind: 'open' | 'download') {
    setBusy(kind);
    try {
      if (kind === 'open') await openReportPdf('weekly', { end });
      else {
        await downloadReportPdf('weekly', `التقرير الأسبوعي ${label}.pdf`, {
          end,
        });
      }
    } catch (error) {
      message.error(
        error instanceof ReportError && error.status === 403
          ? 'التقرير الأسبوعي للجنة وأمين المال فقط.'
          : 'تعذّر تحميل التقرير. أعد المحاولة.',
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card
      title="التقرير الأسبوعي"
      hint="المداخيل حسب المحصِّل، التحصيل في الأشهر الأخيرة، المتأخرون، رسائل واتساب، وتغييرات الحالة. يُرسَل كل اثنين لأعضاء اللجنة."
    >
      <Select
        value={end}
        onChange={setEnd}
        options={choices.map((c) => ({ value: c.end, label: c.label }))}
      />
      <div className="desk-row-actions">
        <Button
          type="primary"
          icon={<FilePdfOutlined />}
          loading={busy === 'open'}
          onClick={() => run('open')}
        >
          فتح
        </Button>
        <Button
          icon={<DownloadOutlined />}
          loading={busy === 'download'}
          onClick={() => run('download')}
        >
          تحميل
        </Button>
      </div>
    </Card>
  );
}

function ArrearsCard() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [{ data, error }] = useQuery({
    query: ArrearsDocument,
    requestPolicy: 'cache-and-network',
  });
  const [filter, setFilter] = useState<ArrearsFilter>('all');
  const [opening, setOpening] = useState(false);
  const all = present(data?.arrearsList?.nodes);
  const rows = filterArrears(all, filter);
  const { ref: fitRef, fit } = useFitColumns<HTMLDivElement>();

  async function openPdf() {
    setOpening(true);
    try {
      await openReportPdf('arrears');
    } catch {
      message.error('تعذّر فتح التقرير. أعد المحاولة.');
    } finally {
      setOpening(false);
    }
  }

  return (
    <Card
      wide
      title="المتأخرون عن الأداء"
      extra={
        <Button
          size="small"
          icon={<FilePdfOutlined />}
          loading={opening}
          onClick={openPdf}
        >
          التقرير الكامل (PDF)
        </Button>
      }
    >
      {error && <Alert type="error" showIcon message="تعذّر تحميل القائمة" />}
      <div className="desk-close-report">
        <strong>{dirhams(totalDue(rows))}</strong>
        <span className="field-hint">· {membersCount(rows.length)}</span>
        <Segmented<ArrearsFilter>
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'all', label: `الكل (${all.length})` },
            {
              value: 'public',
              label: `اللائحة العامة (${filterArrears(all, 'public').length})`,
            },
            {
              value: 'unreachable',
              label: `بدون هاتف (${filterArrears(all, 'unreachable').length})`,
            },
          ]}
        />
      </div>
      <div ref={fitRef}>
        <Table<ArrearRow>
          rowKey="memberId"
          size="small"
          tableLayout="fixed"
          loading={!data && !error}
          dataSource={rows}
          pagination={{ pageSize: 25, size: 'small' }}
          rowClassName={() => 'is-clickable'}
          onRow={(r) => ({
            onClick: () => navigate(`/members/${r.memberId}`),
          })}
          columns={fit<ArrearRow>([
            {
              key: 'name',
              title: 'العضو',
              ellipsis: true,
              dataIndex: 'fullName',
            },
            {
              key: 'bills',
              drop: 3,
              title: 'الفواتير',
              render: (_, r) =>
                `${billsCount(r.billsCount)}${r.oldestBillMonth ? ` منذ ${monthYear(r.oldestBillMonth)}` : ''}`,
            },
            {
              key: 'late',
              drop: 2,
              title: 'التأخر',
              width: 120,
              render: (_, r) =>
                r.daysOverdue > 0 ? daysCount(r.daysOverdue) : '—',
            },
            {
              key: 'tags',
              drop: 1,
              title: '',
              width: 200,
              render: (_, r) => (
                <span className="desk-tags">
                  {r.publiclyListed && <Tag color="red">في اللائحة العامة</Tag>}
                  {(r.phoneStatus !== 'OK' || r.notifyChannel === 'NONE') && (
                    <Tag>بدون هاتف</Tag>
                  )}
                </span>
              ),
            },
            {
              key: 'amount',
              title: 'المبلغ',
              width: 130,
              render: (_, r) => <strong>{dirhams(r.amountDue)}</strong>,
            },
          ])}
        />
      </div>
    </Card>
  );
}

/**
 * Today's reminders (arqam doc 14): «واتساب» opens the ready message (WhatsApp
 * Web or Desktop on a computer) and records the reminder.
 */
function RemindersCard() {
  const { message } = App.useApp();
  const [{ data, error }, refetch] = useQuery({
    query: RemindersDueDocument,
    requestPolicy: 'network-only',
  });
  const [, record] = useMutation(RecordReminderDocument);
  const [done, setDone] = useState<Set<string>>(new Set());
  const reminders = present(data?.remindersDue?.nodes);

  function recordSent(r: ReminderRow, channel: 'WHATSAPP' | 'SMS') {
    setDone((d) => new Set(d).add(r.memberId));
    record({
      memberId: r.memberId,
      channel,
      message: channel === 'WHATSAPP' ? r.whatsappMessage : r.smsMessage,
    }).then((result) => {
      if (result.error) {
        setDone((d) => {
          const next = new Set(d);
          next.delete(r.memberId);
          return next;
        });
        message.error(describeError(result.error, 'لم يُسجَّل التذكير.'));
      }
    });
  }

  return (
    <Card
      wide
      title={`تذكيرات اليوم (${reminders.length})`}
      hint="«واتساب» يفتح الرسالة جاهزة (واتساب ويب أو تطبيق الحاسوب) ويسجّل التذكير. من الهاتف: تطبيق اللجنة، «التقارير»."
      extra={
        done.size > 0 && (
          <Button
            size="small"
            onClick={() => {
              setDone(new Set());
              refetch({ requestPolicy: 'network-only' });
            }}
          >
            تحديث القائمة
          </Button>
        )
      }
    >
      {error && <Alert type="error" showIcon message="تعذّر تحميل القائمة" />}
      {!data && !error && <Skeleton active />}
      {data && reminders.length === 0 && (
        <div className="field-hint">لا أحد يجب تذكيره اليوم.</div>
      )}
      {reminders.map((r) => (
        <div key={r.memberId} className="desk-line">
          <span className="desk-line-text">
            <strong>{r.fullName}</strong>{' '}
            {r.kind === 'LATE_NOTICE' ? (
              <Tag color="red">متأخر {daysCount(r.daysOverdue)}</Tag>
            ) : (
              <Tag color="orange">تذكير</Tag>
            )}
            <span className="field-hint">
              {dirhams(r.amountDue)} · {billsCount(r.billsCount)}
              {r.lastRemindedAt
                ? ` · آخر تذكير ${fullDate(r.lastRemindedAt)}`
                : ' · لم يُذكَّر من قبل'}
            </span>
          </span>
          {done.has(r.memberId) ? (
            <span className="desk-done">
              <CheckCircleFilled /> سُجّل
            </span>
          ) : r.whatsappUrl || r.smsUrl ? (
            <span className="desk-row-actions">
              {r.whatsappUrl && (
                <Button
                  size="small"
                  href={r.whatsappUrl}
                  target="_blank"
                  icon={<WhatsAppOutlined />}
                  onClick={() => recordSent(r, 'WHATSAPP')}
                >
                  واتساب
                </Button>
              )}
              {r.smsUrl && (
                <Button
                  size="small"
                  href={r.smsUrl}
                  icon={<MessageOutlined />}
                  onClick={() => recordSent(r, 'SMS')}
                >
                  SMS
                </Button>
              )}
            </span>
          ) : (
            <a href={r.noticeLink} target="_blank" rel="noreferrer">
              إشعار الأداء
            </a>
          )}
        </div>
      ))}
    </Card>
  );
}
